import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  RefreshCw,
  X,
  User,
  Calendar,
  AlertCircle,
  ChevronDown
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    case 'approved':
      return 'bg-green-100 text-green-800 border border-green-300';
    case 'rejected':
      return 'bg-red-100 text-red-800 border border-red-300';
    default:
      return 'bg-gray-100 text-gray-700 border border-gray-300';
  }
};

const buildQueryParams = (filters) => {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

// ─── StatsCards ─────────────────────────────────────────────────────────────

const StatsCards = ({ stats, loading, activeStatus, onCardClick }) => {
  const cards = [
    {
      label: 'Total',
      value: stats?.total ?? '—',
      filter: 'all',
      icon: Filter,
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconColor: 'text-blue-500',
      valueColor: 'text-blue-700'
    },
    {
      label: 'Pending',
      value: stats?.pending ?? '—',
      filter: 'pending',
      icon: Clock,
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      iconColor: 'text-yellow-500',
      valueColor: 'text-yellow-700'
    },
    {
      label: 'Approved',
      value: stats?.approved ?? '—',
      filter: 'approved',
      icon: CheckCircle,
      bg: 'bg-green-50',
      border: 'border-green-200',
      iconColor: 'text-green-500',
      valueColor: 'text-green-700'
    },
    {
      label: 'Rejected',
      value: stats?.rejected ?? '—',
      filter: 'rejected',
      icon: XCircle,
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconColor: 'text-red-500',
      valueColor: 'text-red-700'
    }
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        const isActive = activeStatus === card.filter;
        return (
          <button
            key={card.filter}
            onClick={() => onCardClick(card.filter)}
            className={`
              text-left p-4 rounded-lg border-2 transition-all duration-150 shadow-sm
              ${card.bg} ${card.border}
              ${isActive ? 'ring-2 ring-offset-1 ring-primary-500 shadow-md' : 'hover:shadow-md'}
            `}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-600">{card.label}</p>
              <Icon className={`h-5 w-5 ${card.iconColor}`} />
            </div>
            <p className={`mt-2 text-2xl font-bold ${card.valueColor}`}>
              {loading ? '—' : card.value}
            </p>
            {isActive && (
              <p className="mt-1 text-xs font-medium text-primary-600">Active filter</p>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── FiltersBar ──────────────────────────────────────────────────────────────

const FiltersBar = ({ filters, onChange, onStatusChange, onApply, onReset, loading }) => (
  <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
    <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end">
      {/* Status */}
      <div className="flex flex-col w-full md:min-w-[140px] md:w-auto">
        <label className="text-xs font-medium text-gray-600 mb-1">Status</label>
        <div className="relative">
          <select
            value={filters.status}
            onChange={(e) => {
              const value = e.target.value;
              onChange('status', value);
              if (onStatusChange) onStatusChange(value);
            }}
            className="w-full appearance-none border border-gray-300 rounded-md text-sm px-3 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Employee ID */}
      <div className="flex flex-col w-full md:min-w-[180px] md:w-auto">
        <label className="text-xs font-medium text-gray-600 mb-1">Employee ID</label>
        <input
          type="text"
          placeholder="Employee ID or name"
          value={filters.userId}
          onChange={(e) => onChange('userId', e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {/* Start Date */}
      <div className="flex flex-col w-full md:min-w-[150px] md:w-auto">
        <label className="text-xs font-medium text-gray-600 mb-1">From Date</label>
        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => onChange('startDate', e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {/* End Date */}
      <div className="flex flex-col w-full md:min-w-[150px] md:w-auto">
        <label className="text-xs font-medium text-gray-600 mb-1">To Date</label>
        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => onChange('endDate', e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-2 w-full md:w-auto">
        <button
          onClick={onApply}
          disabled={loading}
          className="flex-1 md:flex-none flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
        >
          <Filter className="h-4 w-4 mr-1.5" />
          Apply
        </button>
        <button
          onClick={onReset}
          disabled={loading}
          className="flex-1 md:flex-none flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Reset
        </button>
      </div>
    </div>
  </div>
);

// ─── RequestsTable ───────────────────────────────────────────────────────────

const RequestsTable = ({ requests, loading, actionLoading, onView, onApprove, onReject }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-12 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!requests.length) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
        <AlertCircle className="h-10 w-10 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">No regularization requests found for selected filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Requested At', 'Employee', 'Attendance Date', 'Original Time', 'Requested Time', 'Reason', 'Status', 'Actions'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {requests.map((req) => {
              const isPending = req.status === 'pending';
              const isActing = actionLoading[req._id];
              return (
                <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                  {/* Requested At */}
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {formatDateTime(req.createdAt)}
                  </td>

                  {/* Employee */}
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          {req.userId?.name || '—'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {req.userId?.employeeId || req.userId?.email || '—'}
                        </p>
                        {req.userId?.organization && (
                          <p className="text-xs text-gray-400">{req.userId.organization}</p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Attendance Date */}
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>{formatDate(req.attendanceId?.date || req.requestedCheckIn)}</span>
                    </div>
                  </td>

                  {/* Original Time */}
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="space-y-0.5">
                      <p className="text-gray-600">
                        <span className="text-xs text-gray-400 mr-1">In:</span>
                        {formatTime(req.originalCheckIn || req.attendanceId?.checkInTime)}
                      </p>
                      <p className="text-gray-600">
                        <span className="text-xs text-gray-400 mr-1">Out:</span>
                        {formatTime(req.originalCheckOut || req.attendanceId?.checkOutTime)}
                      </p>
                    </div>
                  </td>

                  {/* Requested Time */}
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="space-y-0.5">
                      <p className="font-medium text-indigo-700">
                        <span className="text-xs text-gray-400 mr-1">In:</span>
                        {formatTime(req.requestedCheckIn)}
                      </p>
                      <p className="font-medium text-indigo-700">
                        <span className="text-xs text-gray-400 mr-1">Out:</span>
                        {formatTime(req.requestedCheckOut)}
                      </p>
                    </div>
                  </td>

                  {/* Reason */}
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px]">
                    <p className="truncate" title={req.reason}>{req.reason || '—'}</p>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusBadgeClass(req.status)}`}>
                      {req.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => onView(req._id)}
                        disabled={!!isActing}
                        className="flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-50 transition-colors"
                        title="Review request"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        Review
                      </button>
                      {isPending && (
                        <>
                          <button
                            onClick={() => onApprove(req)}
                            disabled={!!isActing}
                            className="flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 transition-colors"
                            title="Approve"
                          >
                            {isActing === 'approve' ? <LoadingSpinner size="sm" /> : <><CheckCircle className="h-3.5 w-3.5 mr-0.5" />Approve</>}
                          </button>
                          <button
                            onClick={() => onReject(req)}
                            disabled={!!isActing}
                            className="flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
                            title="Reject"
                          >
                            {isActing === 'reject' ? <LoadingSpinner size="sm" /> : <><XCircle className="h-3.5 w-3.5 mr-0.5" />Reject</>}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── ConfirmApproveModal ─────────────────────────────────────────────────────

const ConfirmApproveModal = ({ request, onClose, onConfirm, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black bg-opacity-40" onClick={!loading ? onClose : undefined} />
    <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
      <div className="flex items-center space-x-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Confirm Approval</h3>
          <p className="text-sm text-gray-500">
            {request?.userId?.name || 'Employee'} — {formatDate(request?.requestedCheckIn)}
          </p>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-5 text-sm text-green-800">
        Approve this attendance modification? This will <strong>update the official attendance record</strong> with the requested times.
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-5">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Requested Check-in</p>
          <p className="font-semibold text-gray-900">{formatTime(request?.requestedCheckIn)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Requested Check-out</p>
          <p className="font-semibold text-gray-900">{formatTime(request?.requestedCheckOut)}</p>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onClose}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
        >
          {loading ? <LoadingSpinner size="sm" /> : 'Yes, Approve'}
        </button>
      </div>
    </div>
  </div>
);

// ─── RejectModal ─────────────────────────────────────────────────────────────

const RejectModal = ({ request, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');

  const handleSubmit = () => {
    if (reason.length > 500) {
      toast.error('Rejection reason must be 500 characters or fewer.');
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={!loading ? onClose : undefined} />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Reject Request</h3>
              <p className="text-sm text-gray-500">
                {request?.userId?.name || 'Employee'} — {formatDate(request?.requestedCheckIn)}
              </p>
            </div>
          </div>
          {!loading && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rejection Reason <span className="text-gray-400 font-normal">(optional, max 500)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Provide a reason for rejection..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
          />
          <p className="text-right text-xs text-gray-400 mt-0.5">{reason.length}/500</p>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? <LoadingSpinner size="sm" /> : 'Reject Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── RequestDetailModal ──────────────────────────────────────────────────────

const RequestDetailModal = ({ requestId, onClose, onApprove, onReject, actionLoading }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    setLoading(true);
    setError(false);
    api
      .get(`/admin/attendance-requests/${requestId}`)
      .then((res) => setDetail(res.data?.request || res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [requestId]);

  const isPending = detail?.status === 'pending';
  const isActing = actionLoading[requestId];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">Request Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {loading && (
            <div className="flex justify-center py-10">
              <LoadingSpinner size="lg" />
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-10">
              <AlertCircle className="h-8 w-8 mx-auto text-red-400 mb-2" />
              <p className="text-gray-500">Failed to load request details.</p>
            </div>
          )}
          {detail && !loading && (
            <div className="space-y-5">
              {/* Status badge */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold capitalize ${getStatusBadgeClass(detail.status)}`}>
                  {detail.status}
                </span>
                <p className="text-xs text-gray-400">Submitted: {formatDateTime(detail.createdAt)}</p>
              </div>

              {/* Employee */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Employee</p>
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{detail.userId?.name || '—'}</p>
                    <p className="text-sm text-gray-500">{detail.userId?.email || '—'}</p>
                    <p className="text-xs text-gray-400">
                      ID: {detail.userId?.employeeId || '—'}
                      {detail.userId?.organization ? ` · ${detail.userId.organization}` : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Attendance Date */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Attendance Date</p>
                  <p className="text-sm font-medium text-gray-800">
                    {formatDate(detail.attendanceId?.date || detail.requestedCheckIn)}
                  </p>
                </div>
              </div>

              {/* Time comparison */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Original Times</p>
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-400 text-xs">In: </span>
                    <span className="font-medium">{formatTime(detail.originalCheckIn || detail.attendanceId?.checkInTime)}</span>
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="text-gray-400 text-xs">Out: </span>
                    <span className="font-medium">{formatTime(detail.originalCheckOut || detail.attendanceId?.checkOutTime)}</span>
                  </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <p className="text-xs font-semibold text-indigo-600 uppercase mb-2">Requested Times</p>
                  <p className="text-sm text-indigo-800">
                    <span className="text-indigo-400 text-xs">In: </span>
                    <span className="font-bold">{formatTime(detail.requestedCheckIn)}</span>
                  </p>
                  <p className="text-sm text-indigo-800 mt-1">
                    <span className="text-indigo-400 text-xs">Out: </span>
                    <span className="font-bold">{formatTime(detail.requestedCheckOut)}</span>
                  </p>
                </div>
              </div>

              {/* Reason */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Reason</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
                  {detail.reason || '—'}
                </p>
              </div>

              {/* Request IP */}
              {detail.requesterIp && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Request IP</p>
                  <p className="text-sm text-gray-600 font-mono">{detail.requesterIp}</p>
                </div>
              )}

              {/* Resolution info */}
              {detail.status === 'approved' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-green-700 uppercase mb-1">Approved</p>
                  <p className="text-sm text-green-800">
                    By: <span className="font-medium">{detail.approvedBy?.name || '—'}</span>
                  </p>
                  <p className="text-sm text-green-700 mt-0.5">
                    At: {formatDateTime(detail.approvedAt)}
                  </p>
                </div>
              )}
              {detail.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-700 uppercase mb-1">Rejected</p>
                  {detail.rejectionReason && (
                    <p className="text-sm text-red-800">
                      Reason: <span className="font-medium">{detail.rejectionReason}</span>
                    </p>
                  )}
                  <p className="text-sm text-red-700 mt-0.5">
                    At: {formatDateTime(detail.rejectedAt || detail.updatedAt)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions for pending requests */}
        {detail && isPending && (
          <div className="px-6 pb-5 flex space-x-3">
            <button
              onClick={() => onApprove(detail)}
              disabled={!!isActing}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {isActing === 'approve' ? <LoadingSpinner size="sm" /> : <><CheckCircle className="h-4 w-4 mr-1.5" />Approve</>}
            </button>
            <button
              onClick={() => onReject(detail)}
              disabled={!!isActing}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isActing === 'reject' ? <LoadingSpinner size="sm" /> : <><XCircle className="h-4 w-4 mr-1.5" />Reject</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const INITIAL_FILTERS = {
  status: 'all',
  userId: '',
  startDate: '',
  endDate: ''
};

const AttendanceModificationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [actionLoading, setActionLoading] = useState({});

  // Detail modal
  const [detailRequestId, setDetailRequestId] = useState(null);

  // Approve confirm modal
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);

  // Reject modal
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/admin/attendance-requests/stats/summary');
      setStats(res.data);
    } catch {
      // non-critical — stats cards just show —
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchRequests = useCallback(async (currentFilters) => {
    setTableLoading(true);
    try {
      const qs = buildQueryParams(currentFilters);
      const res = await api.get(`/admin/attendance-requests${qs}`);
      setRequests(res.data?.requests || res.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load attendance requests.';
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchRequests(appliedFilters);
  }, [appliedFilters, fetchRequests]);

  // ── Filter handlers ────────────────────────────────────────────────────────

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };

  const handleStatusFilterChange = (status) => {
    const updated = { ...filters, status };
    setFilters(updated);
    setAppliedFilters(updated);
  };

  const handleCardClick = (status) => {
    const updated = { ...filters, status };
    setFilters(updated);
    setAppliedFilters(updated);
  };

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleApproveClick = (req) => {
    setApproveTarget(req);
  };

  const handleRejectClick = (req) => {
    setRejectTarget(req);
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    const id = approveTarget._id;
    setApproveLoading(true);
    setActionLoading((prev) => ({ ...prev, [id]: 'approve' }));
    try {
      await api.put(`/admin/attendance-requests/${id}/approve`);
      toast.success('Attendance modification request approved.');
      setApproveTarget(null);
      fetchStats();
      fetchRequests(appliedFilters);
      // Refresh detail if same request is open
      if (detailRequestId === id) {
        setDetailRequestId(null);
        setTimeout(() => setDetailRequestId(id), 50);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to approve request.';
      toast.error(msg);
    } finally {
      setApproveLoading(false);
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const confirmReject = async (reason) => {
    if (!rejectTarget) return;
    const id = rejectTarget._id;
    setRejectLoading(true);
    setActionLoading((prev) => ({ ...prev, [id]: 'reject' }));
    try {
      await api.put(`/admin/attendance-requests/${id}/reject`, reason ? { rejectionReason: reason } : {});
      toast.success('Attendance modification request rejected.');
      setRejectTarget(null);
      fetchStats();
      fetchRequests(appliedFilters);
      if (detailRequestId === id) {
        setDetailRequestId(null);
        setTimeout(() => setDetailRequestId(id), 50);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reject request.';
      toast.error(msg);
    } finally {
      setRejectLoading(false);
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Regularization Requests</h1>
          <p className="text-gray-500 text-sm mt-0.5">Review and approve employee attendance regularization requests</p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchRequests(appliedFilters); }}
          className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <StatsCards
        stats={stats}
        loading={statsLoading}
        activeStatus={appliedFilters.status || 'all'}
        onCardClick={handleCardClick}
      />

      {/* Filters */}
      <FiltersBar
        filters={filters}
        onChange={handleFilterChange}
        onStatusChange={handleStatusFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        loading={tableLoading}
      />

      {/* Table */}
      <RequestsTable
        requests={requests}
        loading={tableLoading}
        actionLoading={actionLoading}
        onView={(id) => setDetailRequestId(id)}
        onApprove={handleApproveClick}
        onReject={handleRejectClick}
      />

      {/* Detail Modal */}
      {detailRequestId && (
        <RequestDetailModal
          requestId={detailRequestId}
          onClose={() => setDetailRequestId(null)}
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          actionLoading={actionLoading}
        />
      )}

      {/* Approve Confirm Modal */}
      {approveTarget && (
        <ConfirmApproveModal
          request={approveTarget}
          onClose={() => !approveLoading && setApproveTarget(null)}
          onConfirm={confirmApprove}
          loading={approveLoading}
        />
      )}

      {/* Reject Modal */}
      { rejectTarget && (
        <RejectModal
          request={rejectTarget}
          onClose={() => !rejectLoading && setRejectTarget(null)}
          onConfirm={confirmReject}
          loading={rejectLoading}
        />
      )}
    </div>
  );
};

export default AttendanceModificationRequests;
